/**
 * Pre-configured axios instance with verbose debug logging.
 *
 * Import `http` instead of bare `axios` in components to get automatic
 * request/response/error logging in the browser console.
 */
import axios from "axios";
import debug from "./debug";
import { getAnonId } from "./anonymousId";
import { getApiKey } from "./apiKey";
import { currentUser, webAuth, mobileAuth } from "./firebaseApp";

const http = axios.create();

// Resolve the anon ID once per page load; reuse for every request.
let anonIdPromise: Promise<string | null> | null = null;
const resolveAnonId = () => {
  if (!anonIdPromise) anonIdPromise = getAnonId();
  return anonIdPromise;
};
window.addEventListener("ev-consent-change", () => { anonIdPromise = null; });

// ── Request interceptor ──────────────────────────────────────────
http.interceptors.request.use(
  async (config) => {
    (config as any)._startTime = performance.now();

    // Attach Firebase UID + email for credits/subscription tracking.
    // currentUser() checks both realms (web + mobile).
    const user = currentUser();
    config.headers = config.headers || {};
    if (user?.uid) {
      (config.headers as Record<string, string>)["X-User-UID"] = user.uid;
    }
    if (user?.email) {
      (config.headers as Record<string, string>)["X-User-Email"] = user.email;
    }
    // Active realm — tells the server which RC project to query for
    // tier/entitlement (strict per-realm: mobile = Atomizer AR project,
    // web = ElectronVisualized project, no union). Web wins on tie.
    if (user) {
      const realm = (webAuth.currentUser && user.uid === webAuth.currentUser.uid)
        ? "web"
        : (mobileAuth.currentUser && user.uid === mobileAuth.currentUser.uid)
          ? "mobile"
          : "web";
      (config.headers as Record<string, string>)["X-EV-Realm"] = realm;
    }
    // Anonymous visitor ID (fingerprint / cookie). Skipped entirely when
    // consent is not granted — the backend falls back to IP + UA hash.
    if (!user?.uid) {
      const anonId = await resolveAnonId();
      if (anonId) {
        (config.headers as Record<string, string>)["X-EV-Anon-Id"] = anonId;
      }
    }
    // Native-app WebView: superuser API key bypasses credit checks
    // server-side (server/credits.py:require_credits).
    const apiKey = getApiKey();
    if (apiKey) {
      (config.headers as Record<string, string>)["X-API-Key"] = apiKey;
    }
    // Dev mode: bypass credits on server
    if ((window as any).__EV_DEV_MODE) {
      (config.headers as Record<string, string>)["X-Dev-Mode"] = "true";
    }

    debug.log(
      "api",
      `>>> ${config.method?.toUpperCase()} ${config.url}`,
      config.params ?? "",
      config.data ? `body=${JSON.stringify(config.data).slice(0, 200)}` : "",
    );
    return config;
  },
  (err) => {
    debug.error("api", "Request setup failed", err);
    return Promise.reject(err);
  },
);

// ── Response interceptor ─────────────────────────────────────────
http.interceptors.response.use(
  (response) => {
    const elapsed = (
      performance.now() - ((response.config as any)._startTime || 0)
    ).toFixed(0);
    const size =
      typeof response.data === "string"
        ? response.data.length
        : JSON.stringify(response.data).length;
    debug.log(
      "api",
      `<<< ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}  (${elapsed}ms, ~${size}B)`,
    );
    // Live credit-count update — server's require_credits decorator
    // echoes the post-deduct counts in headers. Header.tsx listens
    // for this event and refreshes the badge without a poll.
    const remaining = response.headers?.["x-credits-remaining"];
    const total = response.headers?.["x-credits-total"];
    if (remaining !== undefined && total !== undefined) {
      window.dispatchEvent(new CustomEvent("ev-credits-update", {
        detail: {
          remaining: Number(remaining),
          total: Number(total),
          tier: response.headers["x-credits-tier"],
        },
      }));
    }
    return response;
  },
  (err) => {
    if (axios.isAxiosError(err) && err.response) {
      const elapsed = (
        performance.now() - ((err.config as any)?._startTime || 0)
      ).toFixed(0);
      debug.error(
        "api",
        `<<< ${err.response.status} ${err.config?.method?.toUpperCase()} ${err.config?.url}  (${elapsed}ms)`,
        err.response.data,
      );
    } else {
      debug.error("api", "Network / timeout error", err.message);
    }
    return Promise.reject(err);
  },
);

export default http;
